import React from 'react';
import PropTypes from 'prop-types';

import { subscribePreviewRefresh } from '../../previewRefresh';

// Internal render width the preview iframe renders at (full-res 1080 on the wide
// axis). The HEIGHT/ratio are derived from the edited screen type so the iframe (a)
// selects the SAME screen type as the editor (ProjectApp picks by nearest ratio) and
// (b) computes the same em-based font size, otherwise cells shift/resize vs the editor.
// The iframe is then CSS-scaled down to fit the pane.
const DEVICE_WIDTH = 1080;

// Collapse rapid successive saves into a single reload.
const REFRESH_DEBOUNCE_MS = 400;

class InlinePreviewPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = { scale: 1, iframeKey: 0 };
    this.containerRef = React.createRef();
    this.refreshTimer = null;
  }

  componentDidMount() {
    this.updateScale();
    window.addEventListener('resize', this.updateScale);
    // Recompute scale when the pane itself changes size (split <-> collapse),
    // which does not fire a window resize event.
    if (typeof ResizeObserver !== 'undefined' && this.containerRef.current) {
      this.resizeObserver = new ResizeObserver(() => this.updateScale());
      this.resizeObserver.observe(this.containerRef.current);
    }
    // Reload the iframe whenever any cell mutation completes.
    this.unsubscribe = subscribePreviewRefresh(this.scheduleReload);
  }

  componentDidUpdate(prevProps) {
    // Screen type change alters the aspect -> recompute scale.
    if (prevProps.screenType !== this.props.screenType) {
      this.updateScale();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateScale);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.unsubscribe) this.unsubscribe();
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
  }

  // Aspect ratio of the edited screen type (falls back to 9:16 portrait).
  aspectRatio() {
    const { screenType } = this.props;
    if (!screenType || !screenType.x_dimension || !screenType.y_dimension) return 9 / 16;
    return screenType.x_dimension / screenType.y_dimension;
  }

  // Internal render height: 1080-wide, height derived from the screen type aspect.
  deviceHeight() {
    return Math.round(DEVICE_WIDTH / this.aspectRatio());
  }

  updateScale = () => {
    const el = this.containerRef.current;
    if (!el) return;
    // Fit the whole render inside the available box: constrain by BOTH the container
    // width and height (minus padding), so nothing overflows/gets cut off.
    const style = window.getComputedStyle(el);
    const padX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const padY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const availableWidth = el.clientWidth - padX;
    const availableHeight = el.clientHeight - padY;
    if (availableWidth <= 0 || availableHeight <= 0) return;
    const scale = Math.min(availableWidth / DEVICE_WIDTH, availableHeight / this.deviceHeight());
    this.setState({ scale });
  };

  scheduleReload = () => {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.setState(({ iframeKey }) => ({ iframeKey: iframeKey + 1 }));
    }, REFRESH_DEBOUNCE_MS);
  };

  render() {
    const { projectId, pageId } = this.props;
    const { scale, iframeKey } = this.state;

    const deviceHeight = this.deviceHeight();

    // The frame's on-screen box is exactly the downscaled render.
    const scaledWidth = DEVICE_WIDTH * scale;
    const scaledHeight = deviceHeight * scale;

    // /preview reads ?width and ?ratio (see ProjectPreview.js). Render at 1080-wide,
    // with the ratio of the EDITED screen type so ProjectApp selects that same screen
    // type (it picks by nearest ratio) and computes the same em font size as the editor.
    // Target the page being edited via /page/:pageId, else it falls through to main page.
    const pagePath = pageId ? `/page/${pageId}` : '';
    const src = `/project/${projectId}/preview${pagePath}?width=${DEVICE_WIDTH}&ratio=${this.aspectRatio()}`;

    return (
      <div className="tsk-inline-preview" ref={this.containerRef}>
        <div className="tsk-inline-preview-frame" style={{ width: scaledWidth, height: scaledHeight }}>
          <iframe
            key={iframeKey}
            title="live-preview"
            src={src}
            className="tsk-inline-preview-iframe"
            style={{
              width: DEVICE_WIDTH,
              height: deviceHeight,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          />
        </div>
      </div>
    );
  }
}

InlinePreviewPanel.propTypes = {
  projectId: PropTypes.string.isRequired,
  pageId: PropTypes.string,
  screenType: PropTypes.object,
};

export default InlinePreviewPanel;
